import { useEffect, useState, useCallback, useMemo } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Box from '@material-ui/core/Box';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

// ── Types matching the HelmChartParameterProcessor output ───────────────────

interface HelmParameter {
  name: string;
  type: string;
  description?: string;
  default?: unknown;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  pattern?: string;
  required?: boolean;
  group?: string;
}

interface ParameterGroup {
  name: string;
  title?: string;
  parameters: string[];
}

interface HelmValuesEditorOptions {
  /** Name of the form field that holds the selected chart entity ref */
  chartEntityRefField?: string;
}

// ── Individual parameter field renderer ─────────────────────────────────────

function ParameterField({
  param,
  value,
  onValueChange,
}: {
  param: HelmParameter;
  value: unknown;
  onValueChange: (name: string, val: unknown) => void;
}) {
  const displayValue = value ?? param.default ?? '';

  // Boolean → Switch
  if (param.type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(displayValue)}
            onChange={e => onValueChange(param.name, e.target.checked)}
            color="primary"
          />
        }
        label={
          <Box>
            <Typography variant="body2">{param.name}</Typography>
            {param.description && (
              <Typography variant="caption" color="textSecondary">
                {param.description}
              </Typography>
            )}
          </Box>
        }
        style={{ marginBottom: 8, width: '100%' }}
      />
    );
  }

  // Enum → Select dropdown
  if (param.enum && param.enum.length > 0) {
    return (
      <FormControl fullWidth style={{ marginBottom: 12 }}>
        <InputLabel shrink>{param.name}</InputLabel>
        <Select
          value={displayValue}
          onChange={e => onValueChange(param.name, e.target.value)}
          displayEmpty
        >
          {param.enum.map(opt => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
        {param.description && (
          <FormHelperText>{param.description}</FormHelperText>
        )}
      </FormControl>
    );
  }

  // Integer / Number → numeric TextField
  if (param.type === 'integer' || param.type === 'number') {
    return (
      <TextField
        label={param.name}
        type="number"
        value={displayValue}
        onChange={e => {
          const raw = e.target.value;
          if (raw === '') {
            onValueChange(param.name, undefined);
          } else {
            onValueChange(
              param.name,
              param.type === 'integer' ? parseInt(raw, 10) : parseFloat(raw),
            );
          }
        }}
        helperText={param.description}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          min: param.minimum,
          max: param.maximum,
        }}
        fullWidth
        style={{ marginBottom: 12 }}
      />
    );
  }

  // Default → string TextField
  return (
    <TextField
      label={param.name}
      value={displayValue}
      onChange={e => onValueChange(param.name, e.target.value)}
      helperText={param.description}
      InputLabelProps={{ shrink: true }}
      fullWidth
      style={{ marginBottom: 12 }}
    />
  );
}

// ── Main HelmValuesEditor component ─────────────────────────────────────────

const HelmValuesEditorComponent = ({
  onChange,
  formData,
  formContext,
  uiSchema,
}: FieldExtensionComponentProps<Record<string, unknown>, HelmValuesEditorOptions>) => {
  const catalogApi = useApi(catalogApiRef);

  const [parameters, setParameters] = useState<HelmParameter[]>([]);
  const [parameterGroups, setParameterGroups] = useState<ParameterGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastEntityRef, setLastEntityRef] = useState<string | null>(null);

  // Read the chart entity ref from the form context (selected on a previous page)
  const chartEntityRefField =
    uiSchema?.['ui:options']?.chartEntityRefField ?? 'helm_chart_repo';

  const chartEntityRef =
    (formContext?.formData as Record<string, unknown> | undefined)?.[
      chartEntityRefField
    ] as string | undefined;

  // Fetch parameters when the selected chart changes
  useEffect(() => {
    if (!chartEntityRef || chartEntityRef === lastEntityRef) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const entity = await catalogApi.getEntityByRef(chartEntityRef);
        if (cancelled) return;

        if (!entity) {
          setError(`Chart entity not found: ${chartEntityRef}`);
          setParameters([]);
          setParameterGroups([]);
          return;
        }

        const spec = entity.spec as Record<string, unknown> | undefined;
        const definition = spec?.definition as Record<string, unknown> | undefined;
        const params = (definition?.parameters ?? []) as HelmParameter[];
        const groups = (definition?.parameterGroups ?? []) as ParameterGroup[];

        setParameters(params);
        setParameterGroups(groups);
        setLastEntityRef(chartEntityRef);

        if (params.length === 0) {
          setError(
            'No parameters found on this chart entity. Parameters will be extracted automatically on the next catalog refresh, or you can enter raw YAML overrides below.',
          );
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(String(e?.message ?? e));
          setParameters([]);
          setParameterGroups([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chartEntityRef, lastEntityRef, catalogApi]);

  // Merge user edits into the formData object
  const currentValues = useMemo(
    () => (formData && typeof formData === 'object' ? formData : {}),
    [formData],
  );

  const handleValueChange = useCallback(
    (name: string, value: unknown) => {
      const updated = { ...currentValues, [name]: value };
      // Remove keys with undefined/empty values to keep overrides clean
      if (value === undefined || value === '') {
        delete updated[name];
      }
      onChange(updated);
    },
    [currentValues, onChange],
  );

  // ── Awaiting chart selection ──────────────────────────────────────
  if (!chartEntityRef) {
    return (
      <Typography variant="body2" color="textSecondary">
        Select a Helm chart on the previous page to configure value overrides.
      </Typography>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 12 }}>
        <CircularProgress size={24} />
        <Typography variant="body2">
          Loading chart parameters from catalog…
        </Typography>
      </Box>
    );
  }

  // ── No parameters: show raw YAML fallback ─────────────────────────
  if (parameters.length === 0) {
    return (
      <Box>
        {error && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}
          >
            {error}
          </Typography>
        )}
        <TextField
          label="Raw YAML Value Overrides"
          multiline
          minRows={10}
          maxRows={30}
          value={currentValues._rawYaml ?? ''}
          onChange={e => handleValueChange('_rawYaml', e.target.value)}
          helperText="Enter YAML key-value pairs to override chart defaults (e.g. image.repository: myregistry.io/app)"
          fullWidth
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    );
  }

  // ── Group parameters into accordion sections ──────────────────────
  const groupedParams = new Map<string, HelmParameter[]>();
  const ungrouped: HelmParameter[] = [];

  // Build group lookup
  const paramToGroup = new Map<string, string>();
  for (const g of parameterGroups) {
    for (const pName of g.parameters) {
      paramToGroup.set(pName, g.name);
    }
  }

  for (const param of parameters) {
    const groupName = paramToGroup.get(param.name) ?? param.group;
    if (groupName) {
      const existing = groupedParams.get(groupName) ?? [];
      existing.push(param);
      groupedParams.set(groupName, existing);
    } else {
      ungrouped.push(param);
    }
  }

  // Group title lookup
  const groupTitles = new Map<string, string>();
  for (const g of parameterGroups) {
    groupTitles.set(g.name, g.title ?? g.name);
  }

  return (
    <Box>
      {error && (
        <Typography
          variant="body2"
          color="error"
          style={{ marginBottom: 12 }}
        >
          {error}
        </Typography>
      )}

      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        {parameters.length} configurable parameters loaded from{' '}
        <strong>{chartEntityRef}</strong>. Only values you change will be
        included in the override file.
      </Typography>

      {/* Ungrouped parameters first */}
      {ungrouped.length > 0 && (
        <Box style={{ marginBottom: 16 }}>
          {ungrouped.map(param => (
            <ParameterField
              key={param.name}
              param={param}
              value={currentValues[param.name]}
              onValueChange={handleValueChange}
            />
          ))}
        </Box>
      )}

      {/* Grouped parameters in accordions */}
      {Array.from(groupedParams.entries()).map(([groupName, params]) => (
        <Accordion key={groupName} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle2">
                {groupTitles.get(groupName) ?? groupName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {params.length} parameter{params.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box style={{ width: '100%' }}>
              {params.map(param => (
                <ParameterField
                  key={param.name}
                  param={param}
                  value={currentValues[param.name]}
                  onValueChange={handleValueChange}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// ── Extension export ────────────────────────────────────────────────────────

export const HelmValuesEditorExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'HelmValuesEditor',
    component: HelmValuesEditorComponent,
  }),
);
